/**
 * <EmployeesDetailModal> — read + edit an employee.
 *
 * Phase-2 Wave-4 W4.T1. Opens with a userId, fetches via `useUserQuery`,
 * renders an identity card (avatar + ФИО + role/dept badges + status) plus
 * a 2-column label/value grid. Toggling «Редактировать» switches the grid
 * to a react-hook-form-driven editor; the field-level matrix is enforced
 * here per Phase-2 RBAC (research-A §2.2; PLAN T11.1):
 *
 *   - Self (route): not used here — the user edits self via /profile.
 *   - РО for own-dept Сотрудник: ФИО / email / phone editable; role +
 *     department disabled.
 *   - Admin (generic ADMIN / ASSISTANT_GIP): manages NON-admin users like a
 *     generic admin — a MANAGER-tier target is fully editable (role clamped to
 *     MANAGER-tier options); an EMPLOYEE-tier target is the РО's job (read-
 *     only); and — under the GLOBAL admin-lifecycle rule — a NON-SELF
 *     ADMIN-tier target is entirely read-only (only a SUPERVISOR manages
 *     admins). Role-change opens the role-transition AlertDialog (Q65).
 *   - SUPERVISOR: the sole admin-appointer — may edit `role` (except on a
 *     supervisor-tier target). Р-114: the option list is NOT admin-tier only
 *     any more — on this EDIT surface it is EVERY assignable role below
 *     supervisor tier (ADMIN + MANAGER + EMPLOYEE), because `PATCH /users/:id`
 *     lets a supervisor assign all of them and the old admin-only clamp made
 *     «повысить рядового в руководители отдела» impossible for every actor.
 *
 * Optimistic-lock 409 path (D5 / V2-FIX-16): on `OPTIMISTIC_LOCK_CONFLICT`
 * the modal refetches and reapplies the user's pending dirty patch onto
 * the fresh server values, surfacing a toast «Запись была изменена.
 * Проверьте поля.» so the user knows their input was preserved but the
 * underlying record moved.
 *
 * 422 surfaces:
 *   - last_admin / last_manager_in_department from PATCH (e.g. demoting
 *     the last admin) → inline alert + toast; the modal stays open so
 *     the user can revert.
 *
 * Footer actions adapt to RBAC + status. Each button is gated through the
 * matching `can*` helper from `../employees-columns` (single source of
 * truth shared with the list-row dropdown), so the FE never surfaces an
 * action the BE will 403 (R6.13 / D-01):
 *   - PENDING:  «Перевыпустить приглашение» (canReissueInvite),
 *               «Удалить» (canHardDelete)
 *   - ACTIVE:   «Сбросить пароль» (canIssueReset),
 *               «Архивировать» (canArchive — Admin→MANAGER-tier,
 *               РО→own-dept EMPLOYEE-tier; everything else hidden)
 *   - ARCHIVED: read-only (no actions)
 */
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Pencil, X } from 'lucide-react';

import {
  ROLE_ACCESS_LEVEL,
  ROLE_NAMES,
  RoleSlug,
  UpdateUserRequestSchema,
  type AccessLevel,
  type UpdateUserRequest,
  type UserDetail,
} from '@aris/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toast';
import { UserRightsBlock } from '@/components/rights/user-rights-block';
import { ApiError } from '@/lib/fetcher';
import { formatDate } from '@/lib/date';
import { canManageRights } from '@/lib/rights-manager';
import { rightsRu } from '@/i18n';

import { useDepartmentsQuery } from '@/api/departments';
import { useRolesQuery } from '@/api/roles';
import { useGipUsersQuery, useUpdateUserMutation, useUserQuery } from '@/api/users';

import { canArchive, canHardDelete, canIssueReset, canReissueInvite } from '../employees-columns';
import {
  RoleTransitionAlertDialog,
  type RoleTransitionContext,
} from './role-transition.alert-dialog';
import { visibleAssignableRoles } from './visible-roles';

// ─── Helpers ─────────────────────────────────────────────────────────────

/** Текст «карточка не открылась» — по тому, что ответил сервер. */
function cardErrorText(err: unknown): string {
  if (err instanceof ApiError && err.status === 404) return rightsRu.card.notFound;
  if (err instanceof ApiError && err.status === 403) return rightsRu.card.forbidden;
  return rightsRu.card.loadError;
}

/** Повтор имеет смысл, только когда отказ — не окончательный ответ сервера. */
function isRetryableCardError(err: unknown): boolean {
  return !(
    err instanceof ApiError &&
    (err.status === 403 || err.status === 404 || err.status === 400)
  );
}

function initialsOf(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase() || '·';
  const a = first[0] ?? '';
  const b = parts[1]?.[0] ?? '';
  return (a + b).toUpperCase() || '·';
}

function statusBadge(status: UserDetail['status']): React.ReactElement {
  if (status === 'ACTIVE') return <Badge variant="active">Активен</Badge>;
  if (status === 'PENDING') return <Badge variant="pending">Ожидает</Badge>;
  return <Badge variant="archived">Архив</Badge>;
}

function tierVariant(level: AccessLevel) {
  if (level === 'ADMIN') return 'admin' as const;
  if (level === 'SUPERVISOR') return 'supervisor' as const;
  if (level === 'MANAGER') return 'manager' as const;
  return 'employee' as const;
}

// ─── RBAC: which fields the actor may edit on the target ─────────────────

export interface DetailActor {
  id: string;
  accessLevel: AccessLevel;
  departmentId: number | null;
  /**
   * The actor's role slug. Under the GLOBAL admin-lifecycle rule an
   * ASSISTANT_GIP edits the directory like a generic admin, so `fieldMatrixFor`
   * no longer branches on this slug — the admin-tier clamp lives on
   * `accessLevel`: only SUPERVISOR sees admin-tier role options, and a
   * non-supervisor admin gets a read-only card (NO_EDIT) on a non-self
   * ADMIN-tier target. Retained for actor-shape symmetry with `AddActor` and
   * possible future per-role UI hooks; optional so minimal callers/tests keep
   * compiling.
   */
  roleSlug?: RoleSlug;
  /**
   * `Role.isAssignable` актора (Ф8 · В-Э, фикс-пак 1) — вместе с уровнем
   * решает, показывать ли блок «Права» (`canManageRights`). Опущено ⇒ блока нет.
   */
  isAssignable?: boolean;
}

export interface FieldEditMatrix {
  fullName: boolean;
  email: boolean;
  phone: boolean;
  /** Admin only. */
  role: boolean;
  /** Admin only. */
  department: boolean;
  /**
   * R6.4 — hire date. Admin edits any non-EMPLOYEE-tier target; РО
   * edits own-dept Сотрудник. EMPLOYEE never edits (matches the BE
   * allowlist).
   */
  hireDate: boolean;
}

/**
 * Pure helper exported so the test file can assert the matrix without
 * rendering the modal. Mirrors PLAN T11.1 + research-A §2.2.
 */
const NO_EDIT: FieldEditMatrix = {
  fullName: false,
  email: false,
  phone: false,
  role: false,
  department: false,
  hireDate: false,
};

export function fieldMatrixFor(actor: DetailActor, target: UserDetail): FieldEditMatrix {
  const archived = target.status === 'ARCHIVED';
  if (archived) {
    return { ...NO_EDIT };
  }
  // GLOBAL admin-lifecycle rule (assistant-parity): an ASSISTANT_GIP is a
  // full generic-admin for NON-admin user management — it edits the directory
  // exactly like a generic ADMIN (no ASSISTANT_GIP short-circuit here). What
  // it — and every non-supervisor admin — cannot do is manage a NON-SELF
  // ADMIN-tier user at all: appointing an admin-tier role is supervisor-only,
  // and (fix-pack) the BE now 403s EVERY write against a non-self admin target
  // (edit fields / deactivate / archive / hard-delete / binding). Only a
  // SUPERVISOR manages admins.
  if (actor.accessLevel === 'ADMIN') {
    // RTF L79 (devil-advocate post-deploy bar 1 + bar 8): Admin must NOT
    // edit Сотрудник-tier (EMPLOYEE) targets — that responsibility belongs
    // to the РО. The BE rejects that PATCH with
    // `admin_cannot_modify_employee_tier`; this matrix mirror hides the edit
    // affordance upfront so we never surface an action that will 403.
    if (target.accessLevel === 'EMPLOYEE') {
      return { ...NO_EDIT };
    }
    // FIX 1 (mirror the BE admin-lifecycle gate): a NON-supervisor admin may
    // not mutate a NON-SELF ADMIN-tier user in any way — the BE now 403s the
    // whole write (not just role). Return NO_EDIT so the card is read-only and
    // no field is surfaced that the BE will reject. Self is exempt: an admin
    // still edits their own contact fields (BE self-edit allowlist =
    // email/phone/fullName) — handled by the fall-through below.
    const targetIsAdminTier = target.accessLevel === 'ADMIN';
    if (targetIsAdminTier && target.id !== actor.id) {
      return { ...NO_EDIT };
    }
    // Non-admin (MANAGER) target — or the admin's OWN card. Contact fields +
    // hireDate editable. `role`/`department` are appointment-/tier-coupled:
    //   - role: appointing/demoting across the admin tier is supervisor-only
    //     and an admin-tier (self) role isn't in the MANAGER-only option list
    //     (`visibleAssignableRoles`), so disable it for an admin-tier target.
    //   - department: an admin-tier row requires `departmentId = NULL`
    //     (DB CHECK `user_dept_match_role`), so it is never editable for an
    //     admin-tier target (FIX 3) — otherwise a save would 500.
    // R6.4 — Admin owns hireDate for Admin/РО-tier targets.
    return {
      fullName: true,
      email: true,
      phone: true,
      role: !targetIsAdminTier,
      department: !targetIsAdminTier,
      hireDate: true,
    };
  }
  if (actor.accessLevel === 'SUPERVISOR') {
    // ADD #2: under the GLOBAL rule the SUPERVISOR is the ONLY actor that may
    // appoint an ADMIN-tier role, so it gets a role-edit affordance the
    // detail modal never offered before (this branch previously fell through
    // to the final NO_EDIT). The BE P3 gate exempts SUPERVISOR and auto-nulls
    // `departmentId` on an admin-tier promotion.
    //
    // Р-114 (2026-09-03): this is the EDIT surface, so the select is built
    // with `visibleAssignableRoles(actor, roles, 'edit')` — it now offers
    // EVERY assignable role below supervisor tier (ADMIN + MANAGER +
    // EMPLOYEE), not the ADMIN-tier four. `PATCH /users/:id` lets a
    // SUPERVISOR assign all of them (the `admin_cannot_*` gates are
    // ADMIN-actor-only), and the previous ADMIN-only clamp made «повысить
    // рядового сотрудника в руководители отдела» impossible for EVERY actor.
    // The invite surface is unchanged — see ./visible-roles.ts.
    //
    // KNOWN LIMIT (долг #274, not fixed here): `department` stays false, so
    // demoting a DEPTLESS admin-tier user DOWN to a dept-mandatory tier
    // (MANAGER/EMPLOYEE) has no dept affordance. The BUG-C pre-guard in
    // `dispatchSave` catches it with an inline «Выберите отдел для этой
    // роли» instead of an opaque 422. The customer's case — a dept-holding
    // rank-and-file employee promoted to РО — keeps its department and is
    // unaffected.
    //
    // FIX 2: EXCEPT on a SUPERVISOR-tier target — the BE 403s a supervisor
    // re-roling a peer supervisor, and no supervisor-tier role is ever
    // listed (the tier filter drops CEO/SYSADMIN), so leaving `role` enabled
    // would both mis-show the «Выберите роль» placeholder and surface a 403.
    // The other fields stay conservative (false): a supervisor edit of
    // contact/dept/hireDate is not established BE-side (`department` also
    // stays false for an admin-tier target — FIX 3, DB dept-null CHECK).
    return {
      fullName: false,
      email: false,
      phone: false,
      role: target.accessLevel !== 'SUPERVISOR',
      department: false,
      hireDate: false,
    };
  }
  if (actor.accessLevel === 'MANAGER') {
    const sameDept = target.departmentId !== null && target.departmentId === actor.departmentId;
    const subordinate = target.accessLevel === 'EMPLOYEE';
    const editable = sameDept && subordinate;
    return {
      fullName: editable,
      email: editable,
      phone: editable,
      role: false,
      department: false,
      // R6.4 — РО owns the Сотрудник joining date for own dept (mirrors
      // the contact-info trust surface). EMPLOYEE never edits.
      hireDate: editable,
    };
  }
  return {
    fullName: false,
    email: false,
    phone: false,
    role: false,
    department: false,
    hireDate: false,
  };
}

// ─── Action callbacks (wired by parent) ──────────────────────────────────

export interface EmployeesDetailModalCallbacks {
  /** Open the «Перевыпустить приглашение» dialog for a PENDING row. */
  onReissueInvite?: (user: UserDetail) => void;
  /** Open the «Сбросить пароль» dialog for an ACTIVE row. */
  onIssueReset?: (user: UserDetail) => void;
  /** Open the archive AlertDialog (ACTIVE rows only). */
  onArchive?: (user: UserDetail) => void;
  /** Open the hard-delete AlertDialog (PENDING rows only). */
  onHardDelete?: (user: UserDetail) => void;
}

export interface EmployeesDetailModalProps extends EmployeesDetailModalCallbacks {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  userId: string | null;
  actor: DetailActor;
}

interface FormValues {
  fullName?: string;
  email?: string;
  phone?: string;
  roleId?: number;
  departmentId?: number | null;
  /** R6.4 — joining date ISO `YYYY-MM-DD`. */
  hireDate?: string;
  /**
   * Feature 1 «Помощник ГИПа» (Wave 5). The GIP this user binds to when the
   * (Admin) actor changes the role TO ASSISTANT_GIP. Empty string = unset.
   * Seeded from the user's existing binding so editing an already-assistant
   * shows the current GIP. Cleared when the role moves away from
   * ASSISTANT_GIP.
   */
  assistantOfUserId?: string;
}

function defaultsFromUser(u: UserDetail | undefined): FormValues {
  if (!u) return {};
  return {
    fullName: u.fullName,
    email: u.email,
    // R6 W1-T1: `User.phone` is now nullable (pending users start
    // phone-less). The form's `phone` field is a controlled text
    // input — coerce NULL to undefined so it renders as the empty
    // string, matching the «not yet supplied» semantics.
    phone: u.phone ?? undefined,
    roleId: u.roleId,
    departmentId: u.departmentId,
    hireDate: u.hireDate,
    // Feature 1 «Помощник ГИПа» (Wave 5). Seed the current binding so
    // editing an already-assistant shows their GIP pre-selected. `null` /
    // `undefined` → empty string (unset).
    assistantOfUserId: u.assistantOfUserId ?? '',
  };
}

const NULL_DEPT = '__none__';

/**
 * ⚠️ Фикс-пак Р-114 (03.09.2026) — ЧЕЛОВЕЧЕСКИЙ ТЕКСТ ВМЕСТО «ТАКАЯ ЗАПИСЬ
 * УЖЕ СУЩЕСТВУЕТ».
 *
 * Триггер `user_one_active_ro_per_dept` (миграция 002) держит инвариант
 * «один действующий РО на отдел» и отвергает вторую строку через
 * `RAISE ... USING ERRCODE = 'unique_violation'`. Дальше отказ проходит
 * общий фильтр (`http-exception.filter.ts`) и приезжает на экран как 409
 * без своего кода — то есть буквально «Такая запись уже существует» на
 * попытке назначить руководителя отдела. На поверхности ПРИГЛАШЕНИЯ тот же
 * инвариант отбивается по-людски (422 `DEPT_HAS_ACTIVE_RO` с именем
 * действующего РО); правка отставала.
 *
 * Текст один на оба места вызова — предпроверку в `onSubmit` и запасную
 * сетку на 409 в `dispatchSave`, — чтобы человек видел одно и то же
 * независимо от того, застала ли его гонка.
 */
const DEPT_HAS_ACTIVE_RO_MSG =
  'В отделе уже есть действующий руководитель. Сначала снимите его с роли или переведите в другой отдел.';

/**
 * Resolver schema for the edit form. Carries the editable subset (the BE
 * schema requires `version`, attached at submit). We normalise the GIP-
 * picker's `''` (unset) state to `undefined` so the underlying
 * `.uuid().nullable().optional()` contract accepts «no pick» and validates a
 * real pick as a uuid. The required-when-becoming-ASSISTANT_GIP rule is
 * enforced at submit (zod can't see the role-change context).
 */
const EditUserResolverSchema = UpdateUserRequestSchema.partial({ version: true }).extend({
  assistantOfUserId: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.string().uuid().nullable().optional(),
  ),
});

// ─── Component ──────────────────────────────────────────────────────────

export function EmployeesDetailModal(props: EmployeesDetailModalProps): React.ReactElement {
  const {
    open,
    onOpenChange,
    userId,
    actor,
    onReissueInvite,
    onIssueReset,
    onArchive,
    onHardDelete,
  } = props;

  const userQuery = useUserQuery(userId ?? undefined);
  const departmentsQuery = useDepartmentsQuery();
  // L79: do NOT request unassignable roles — Admin has no business seeing
  // SUPERVISOR/seed-only entries in the edit select (devil-advocate
  // post-deploy bar 9). The shared `visibleAssignableRoles` helper then
  // narrows further, on the 'edit' surface: Admin sees MANAGER-tier only,
  // MANAGER's list is inert (matrix `role: false`), SUPERVISOR sees every
  // tier below supervisor (Р-114).
  //
  // NB this flag is NOT what keeps supervisor-tier roles out of the list:
  // CEO is SUPERVISOR-tier AND `isAssignable = true` (only SYSADMIN is
  // false in the seed), so `includeUnassignable: false` still returns it.
  // The tier filter inside the helper is what excludes it.
  const rolesQuery = useRolesQuery({ includeUnassignable: false });
  const updateMutation = useUpdateUserMutation();

  const user = userQuery.data;
  const matrix = React.useMemo<FieldEditMatrix>(
    () =>
      user
        ? fieldMatrixFor(actor, user)
        : {
            fullName: false,
            email: false,
            phone: false,
            role: false,
            department: false,
            hireDate: false,
          },
    [actor, user],
  );
  const anyEditable =
    matrix.fullName ||
    matrix.email ||
    matrix.phone ||
    matrix.role ||
    matrix.department ||
    matrix.hireDate;

  const [editing, setEditing] = React.useState(false);
  const [pendingTransition, setPendingTransition] =
    React.useState<RoleTransitionContext<FormValues> | null>(null);
  const [formError, setFormError] = React.useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(EditUserResolverSchema),
    defaultValues: {},
    mode: 'onSubmit',
  });

  // Whenever the underlying user is refetched (initial load OR post-409),
  // reseed the form with the fresh row. This is the optimistic-lock
  // 409 retry path: previously-dirty fields stay dirty (RHF preserves
  // them across `reset` when we use the keepValues option), so the user
  // does not lose their pending edit.
  const lastUserVersion = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (!user) return;
    if (lastUserVersion.current === user.version) return;
    lastUserVersion.current = user.version;
    form.reset(defaultsFromUser(user), {
      keepDirtyValues: true,
      keepErrors: true,
      keepIsSubmitted: false,
    });
  }, [user, form]);

  // When the modal closes, drop local state so a future open is fresh.
  React.useEffect(() => {
    if (!open) {
      setEditing(false);
      setPendingTransition(null);
      setFormError(null);
      lastUserVersion.current = null;
    }
  }, [open]);

  // Feature 1 «Помощник ГИПа» (Wave 5). Watch the selected role and resolve
  // its slug via the roles list so we can show the GIP-picker when the Admin
  // changes the role TO ASSISTANT_GIP. The picker is required in that case
  // (mirrors BE `ASSISTANT_BINDING_REQUIRED`); changing AWAY clears the
  // binding (the BE nulls it server-side, we just stop sending a value).
  const selectedRoleId = form.watch('roleId');
  const selectedRoleSlug = React.useMemo<RoleSlug | undefined>(
    () => rolesQuery.data?.find((r) => r.id === selectedRoleId)?.slug,
    [rolesQuery.data, selectedRoleId],
  );
  const isAssistantGipSelected = selectedRoleSlug === RoleSlug.ASSISTANT_GIP;
  const gipUsersQuery = useGipUsersQuery({ enabled: editing && isAssistantGipSelected });
  const gipOptions = React.useMemo(() => gipUsersQuery.data?.rows ?? [], [gipUsersQuery.data]);

  // cr MED-2 fix-pack: the GIP list is ACTIVE-only. If the user is already an
  // ASSISTANT_GIP whose bound GIP was since archived, the seeded id won't be
  // in `gipOptions` and the picker would render the placeholder — looking
  // "unset" when it is actually bound to an archived GIP. Append the stale
  // binding as a labeled option (only when it's missing AND we're editing an
  // already-assistant) so the current value stays visible and re-submittable.
  const seededBinding = user?.assistantOfUserId ?? null;
  const gipPickerOptions = React.useMemo(() => {
    if (!seededBinding) return gipOptions;
    if (gipOptions.some((u) => u.id === seededBinding)) return gipOptions;
    // The query may still be loading; once it resolves and the id is present
    // this branch drops out. Surface a clear "archived" label meanwhile.
    return [...gipOptions, { id: seededBinding, fullName: 'Текущий ГИП (архивирован)' }];
  }, [gipOptions, seededBinding]);

  const onCancelEdit = React.useCallback(() => {
    setEditing(false);
    setFormError(null);
    if (user) form.reset(defaultsFromUser(user));
  }, [form, user]);

  const dispatchSave = React.useCallback(
    async (raw: FormValues) => {
      if (!user) return;
      // Build the patch from the dirty subset only — the BE reconciles
      // unset keys to "no change". `version` is mandatory.
      const patch: UpdateUserRequest = { version: user.version };
      if (matrix.fullName && raw.fullName !== undefined && raw.fullName !== user.fullName) {
        patch.fullName = raw.fullName;
      }
      if (matrix.email && raw.email !== undefined && raw.email !== user.email) {
        patch.email = raw.email;
      }
      if (matrix.phone && raw.phone !== undefined && raw.phone !== user.phone) {
        patch.phone = raw.phone;
      }
      if (matrix.role && raw.roleId !== undefined && raw.roleId !== user.roleId) {
        patch.roleId = raw.roleId;
      }
      if (matrix.department && raw.departmentId !== user.departmentId) {
        patch.departmentId = raw.departmentId ?? null;
      }
      // R6.4 — only emit hireDate when the value actually changed.
      if (matrix.hireDate && raw.hireDate !== undefined && raw.hireDate !== user.hireDate) {
        patch.hireDate = raw.hireDate;
      }
      // Feature 1 «Помощник ГИПа» (Wave 5). Resolve the assistant binding
      // from the EFFECTIVE post-PATCH role (the new role if changing, else
      // the current one). The role select is Admin-only (`matrix.role`); for
      // a MANAGER actor `matrix.role` is false so this block is inert.
      if (matrix.role) {
        const effectiveRoleId = patch.roleId ?? user.roleId;
        const effectiveSlug = rolesQuery.data?.find((r) => r.id === effectiveRoleId)?.slug;
        const wasAssistant = user.roleSlug === RoleSlug.ASSISTANT_GIP;
        if (effectiveSlug === RoleSlug.ASSISTANT_GIP) {
          // TO / STAY ASSISTANT_GIP: a valid GIP pick is required. Mirror the
          // BE `ASSISTANT_BINDING_REQUIRED` with an inline field error.
          const picked = raw.assistantOfUserId ?? '';
          if (!picked) {
            form.setError('assistantOfUserId', {
              message: 'Укажите ГИПа, которому назначается помощник',
            });
            return;
          }
          // Send the binding when it is a fresh assignment OR the pick
          // actually changed (omission ≠ change for an already-assistant).
          if (!wasAssistant || picked !== (user.assistantOfUserId ?? '')) {
            patch.assistantOfUserId = picked;
          }
        } else if (wasAssistant && patch.roleId !== undefined) {
          // FROM ASSISTANT_GIP to another role: the BE clears the binding in
          // the same atomic write, so we needn't send a value — but we send
          // an explicit `null` to make the intent unambiguous and match the
          // BE's idempotent clear path.
          patch.assistantOfUserId = null;
        }

        // Feature 1 Wave 5 fix-pack BUG-C: a dept-mandatory new role
        // (EMPLOYEE / MANAGER tier) REQUIRES a department. An ASSISTANT_GIP
        // (ADMIN-tier) carries `departmentId = null`, so demoting them to such
        // a tier without choosing a dept leaves both old + new dept null — the
        // FE omits the field and the BE hits the DB CHECK `user_dept_match_role`
        // → an opaque 422. Pre-guard here (mirroring the binding pre-guard):
        // inline-require the dept BEFORE dispatch. The dept Select is already
        // shown + enabled for this transition (`matrix.department` is true for
        // an ADMIN actor editing the still-ASSISTANT_GIP target).
        if (effectiveSlug && effectiveSlug !== RoleSlug.ASSISTANT_GIP) {
          const effectiveLevel = ROLE_ACCESS_LEVEL[effectiveSlug as RoleSlug];
          const deptMandatory = effectiveLevel === 'EMPLOYEE' || effectiveLevel === 'MANAGER';
          // Effective dept after this PATCH: the body value if the dept field
          // was touched, else the current value.
          const effectiveDeptId =
            patch.departmentId !== undefined ? patch.departmentId : user.departmentId;
          if (deptMandatory && (effectiveDeptId === null || effectiveDeptId === undefined)) {
            form.setError('departmentId', { message: 'Выберите отдел для этой роли' });
            return;
          }
        }
      }
      // ⚠️ Фикс-пак Р-114 — ГЕЙТ запасной сетки на 409 (сама сетка ниже, в
      // `catch`). Гейт несущий, а не украшение: 409 `CONFLICT` (P2002)
      // приезжает и на занятый email — `UsersService.update` предпроверки
      // email не делает вовсе, и уникальный индекс `User.email` даёт ровно
      // тот же код. Без гейта правка одного email отвечала бы «В отделе уже
      // есть действующий руководитель» — новая ложь вместо старой. Поэтому
      // сетка срабатывает, только когда запись ДЕЙСТВИТЕЛЬНО метит роль или
      // отдел и приземляется на MANAGER-ярус.
      const managerTierWrite =
        (patch.roleId !== undefined || patch.departmentId !== undefined) &&
        ROLE_ACCESS_LEVEL[
          (rolesQuery.data?.find((r) => r.id === (patch.roleId ?? user.roleId))?.slug ??
            user.roleSlug) as RoleSlug
        ] === 'MANAGER';
      try {
        await updateMutation.mutateAsync({ id: user.id, patch });
        toast('Изменения сохранены', { variant: 'success' });
        setEditing(false);
        setFormError(null);
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.status === 409 && err.code === 'OPTIMISTIC_LOCK_CONFLICT') {
            toast('Запись была изменена. Проверьте поля.', { variant: 'warning' });
            // Refetch — `keepDirtyValues: true` in the effect above
            // preserves the user's in-flight edits on top of fresh
            // server values.
            await userQuery.refetch();
            return;
          }
          // ⚠️ Фикс-пак Р-114 — ЗАПАСНАЯ СЕТКА под предпроверкой «в отделе
          // уже есть РО». Предпроверка в `onSubmit` — СНИМОК списка отделов,
          // и гонку она не закрывает: два супервайзера в двух вкладках,
          // вкладка, ночевавшая открытой (`hasActiveManager` кэшируется на
          // 5 минут — `useDepartmentsQuery`). Тогда отказ приходит от базы, и
          // показать его надо тем же текстом, а не «Такая запись уже
          // существует».
          //
          // ⚠️ Кодов ДВА, и они НЕ равнозначны — порядок такой:
          //   • `CONFLICT` — рабочий путь. Триггер поднимает
          //     `unique_violation` БЕЗ имени ограничения, и Prisma
          //     отображает это в P2002 с пустым `meta.target`; общий фильтр
          //     превращает P2002 в 409 `CONFLICT` «Такая запись уже
          //     существует». Прочитано, а не угадано: тот же триггер уже
          //     разбирается по тому же признаку на пути активации
          //     приглашения — `apps/api/src/invites/invites.service.ts:558-571`
          //     («raises ... USING ERRCODE='unique_violation' which Prisma
          //     maps to P2002 with NO `meta.target`»), и там отказ
          //     дополнительно опознаётся по подстроке «active MANAGER».
          //   • `unique_constraint_violation` — сетка под сеткой: тот же
          //     SQLSTATE 23505, дошедший до фильтра сырым, минуя
          //     классификацию Prisma. Стои́т дёшево, и без него редкий путь
          //     снова показал бы «Такая запись уже существует».
          if (
            err.status === 409 &&
            managerTierWrite &&
            (err.code === 'CONFLICT' || err.code === 'unique_constraint_violation')
          ) {
            form.setError(patch.roleId !== undefined ? 'roleId' : 'departmentId', {
              message: DEPT_HAS_ACTIVE_RO_MSG,
            });
            return;
          }
          if (
            err.status === 403 &&
            (err.code === 'admin_cannot_modify_employee_tier' ||
              err.code === 'admin_cannot_assign_employee_role')
          ) {
            // RTF L79 — Admin tried to PATCH an EMPLOYEE-tier user OR
            // assign an EMPLOYEE-tier role. The FE matrix should hide
            // these affordances; this toast is the defense-in-depth path
            // (e.g. role list raced behind a stale roles cache).
            const msg =
              'Только РО управляет сотрудниками этого уровня. Обратитесь к руководителю отдела.';
            setFormError(msg);
            toast(msg, { variant: 'error' });
            return;
          }
          if (
            err.status === 422 &&
            (err.code === 'last_admin' ||
              err.code === 'last_manager_in_department' ||
              err.code === 'last_admin_gip')
          ) {
            // ⚠️ Фикс-пак Р-114 — ДВА ИЗ ТРЁХ ТЕКСТОВ ПОДМЕНЯЛИ ОТКАЗ ЧУЖИМ
            // СМЫСЛОМ, и человек читал не ту причину:
            //   • `last_manager_in_department` показывался как «это последний
            //     РО отдела», а сервер отвечает «Пользователь руководит
            //     отделом хотя бы в одном проекте. Сначала назначьте туда
            //     другого руководителя» — отказ про строки `ProjectManager`,
            //     а не про численность РО в отделе; условие отказа —
            //     `projectManager.count({ roUserId })`, отдел в нём не
            //     участвует вовсе (`users.service.ts`, ветка `roleId`);
            //   • `last_admin_gip` показывался как «это последний активный
            //     ГИП», а сервер отвечает «Пользователь ведёт проекты как
            //     ГИП. Сначала передайте его проекты другому ГИПу» — условие
            //     `project.count({ gipId })`, и «последний» там тоже ни при
            //     чём: отказ придёт и десятому ГИПу с одним проектом.
            // Оба показываем СЕРВЕРНЫМ текстом — он называет и причину, и
            // действие, а держать вторую копию значит однажды разойтись.
            //
            // ⚠️ `last_admin` оставлен литералом СОЗНАТЕЛЬНО: этот код живёт
            // только в `archive()`, из `update()` он недостижим (проверки
            // последнего администратора в `update()` НЕТ вовсе — см. долг
            // #274), то есть ветка здесь — оборона в глубину, а не рабочий
            // путь. Асимметрия названа, чтобы не выглядеть недоделкой.
            const msg =
              err.code === 'last_admin'
                ? 'Невозможно изменить: это последний активный администратор.'
                : err.message;
            setFormError(msg);
            toast(msg, { variant: 'error' });
            return;
          }
          // Feature 1 «Помощник ГИПа» (Wave 5). Surface the BE's binding
          // errors inline on the GIP-picker field (defense in depth — the FE
          // guards the required-pick, but e.g. the picked GIP could be
          // archived between fetch and submit → TARGET_INVALID).
          //
          // UI MED fix-pack: inline ONLY (no `setFormError` banner) — showing
          // the same message in both the field <FormMessage> and the banner
          // duplicated it. The banner is reserved for non-field problems
          // (GIP_OWNS_PROJECTS / GIP_HAS_ASSISTANTS below).
          if (
            err.status === 422 &&
            (err.code === 'ASSISTANT_BINDING_REQUIRED' ||
              err.code === 'ASSISTANT_BINDING_TARGET_INVALID' ||
              err.code === 'ASSISTANT_BINDING_NOT_ALLOWED')
          ) {
            form.setError('assistantOfUserId', { message: err.message });
            return;
          }
          // Feature 1 Wave 5 fix-pack BUG-C (fallback): the DB CHECK
          // `user_dept_match_role` surfaces as 422 `check_constraint_violation`
          // with `details.constraint`. This is the defense-in-depth net under
          // the dept pre-guard (e.g. a race that bypassed the inline check) —
          // map it to a clean inline dept error instead of a raw toast.
          if (
            err.status === 422 &&
            err.code === 'check_constraint_violation' &&
            (err.envelope?.error?.details as { constraint?: string } | undefined)?.constraint ===
              'user_dept_match_role'
          ) {
            form.setError('departmentId', { message: 'Выберите отдел для этой роли' });
            return;
          }
          // GIP_OWNS_PROJECTS / GIP_HAS_ASSISTANTS — the Admin tried to
          // demote / convert a GIP that still owns projects or has bound
          // assistants. These are blocking pre-conditions, not field-level
          // problems; surface the BE's actionable message («переназначьте
          // проекты» / «отвяжите помощников») as a form-level alert + toast.
          if (
            err.status === 422 &&
            (err.code === 'GIP_OWNS_PROJECTS' || err.code === 'GIP_HAS_ASSISTANTS')
          ) {
            setFormError(err.message);
            toast(err.message, { variant: 'error' });
            return;
          }
          setFormError(err.message);
          toast(err.message, { variant: 'error' });
          return;
        }
        setFormError('Не удалось сохранить изменения.');
      }
    },
    [user, matrix, updateMutation, userQuery, rolesQuery.data, form],
  );

  const onSubmit = React.useCallback(
    async (raw: FormValues) => {
      if (!user) return;
      // If the role changed AND actor is Admin, route through the
      // role-transition AlertDialog (Q65) before dispatching PATCH.
      const roleChanged = matrix.role && raw.roleId !== undefined && raw.roleId !== user.roleId;
      const deptChanged = matrix.department && raw.departmentId !== user.departmentId;

      // ⚠️ Фикс-пак Р-114 — ПРЕДПРОВЕРКА «В ОТДЕЛЕ УЖЕ ЕСТЬ ДЕЙСТВУЮЩИЙ РО».
      // Стоит ДО ветки диалога намеренно: занятый отдел — это не «подтвердите
      // перевод», а отказ, и показывать сперва диалог о повышении, чтобы
      // отказать после подтверждения, значит врать дважды.
      //
      // Данные уже в модалке: `useDepartmentsQuery` зовётся выше ради
      // выпадающего списка отделов, а `hasActiveManager` есть в договоре
      // `DepartmentRow` (тем же полем пользуется форма приглашения, которая
      // прячет занятые отделы из списка).
      //
      // Ярус, а не слаг: триггер миграции 002 стои́т на
      // `role_access_level(NEW."roleId") = 'MANAGER'`, поэтому и здесь
      // сравнивается ярус — новый MANAGER-слаг не проскочит мимо проверки.
      //
      // ⚠️ Цель, которая САМА и есть действующий РО этого отдела, не
      // блокируется: триггер исключает себя (`u.id <> NEW.id`), и без этого
      // условия любая правка карточки живого РО упиралась бы в его же
      // собственную строку.
      //
      // ⚠️ PENDING-цель проверяется НАРАВНЕ с активной, и это не оплошность:
      // триггер неактивных не видит (`NEW.status <> 'ACTIVE'` → выход), и
      // серверу такое назначение пройдёт молча — отказ прилетит приглашённому
      // при активации, чужим языком (долг #275). Предпроверке же важно
      // другое: в отделе есть ЖИВОЙ руководитель, и второго там не будет.
      if (roleChanged || deptChanged) {
        const nextRoleId = roleChanged ? raw.roleId : user.roleId;
        const nextRoleSlug =
          rolesQuery.data?.find((r) => r.id === nextRoleId)?.slug ?? user.roleSlug;
        const nextDeptId = deptChanged ? (raw.departmentId ?? null) : user.departmentId;
        const targetIsThisDeptRo =
          user.accessLevel === 'MANAGER' &&
          user.status === 'ACTIVE' &&
          user.departmentId === nextDeptId;
        const nextDept =
          nextDeptId === null || nextDeptId === undefined
            ? undefined
            : departmentsQuery.data?.find((d) => d.id === nextDeptId);
        if (
          ROLE_ACCESS_LEVEL[nextRoleSlug as RoleSlug] === 'MANAGER' &&
          nextDept?.hasActiveManager === true &&
          !targetIsThisDeptRo
        ) {
          form.setError(roleChanged ? 'roleId' : 'departmentId', {
            message: DEPT_HAS_ACTIVE_RO_MSG,
          });
          return;
        }
      }

      if (roleChanged || deptChanged) {
        const fromRole = rolesQuery.data?.find((r) => r.id === user.roleId);
        const toRole = rolesQuery.data?.find((r) => r.id === raw.roleId);
        const fromDept = user.departmentId
          ? (departmentsQuery.data?.find((d) => d.id === user.departmentId) ?? null)
          : null;
        const toDept =
          raw.departmentId && raw.departmentId !== null
            ? (departmentsQuery.data?.find((d) => d.id === raw.departmentId) ?? null)
            : null;
        setPendingTransition({
          fromRoleSlug: fromRole?.slug ?? user.roleSlug,
          toRoleSlug: toRole?.slug ?? user.roleSlug,
          fromRoleName: fromRole?.name ?? ROLE_NAMES[user.roleSlug],
          toRoleName: toRole?.name ?? ROLE_NAMES[user.roleSlug],
          targetFullName: user.fullName,
          fromDeptName: fromDept?.name ?? null,
          toDeptName: toDept?.name ?? null,
          pending: raw,
        });
        return;
      }
      await dispatchSave(raw);
    },
    [user, matrix, rolesQuery.data, departmentsQuery.data, dispatchSave, form],
  );

  const onTransitionConfirm = React.useCallback(async () => {
    if (!pendingTransition) return;
    const pending = pendingTransition.pending;
    setPendingTransition(null);
    await dispatchSave(pending);
  }, [pendingTransition, dispatchSave]);

  const onTransitionCancel = React.useCallback(() => {
    if (!pendingTransition) return;
    // Revert the role / dept to its on-server value so the form returns
    // to the un-dirty state for those fields.
    if (user) {
      form.setValue('roleId', user.roleId, { shouldDirty: false });
      form.setValue('departmentId', user.departmentId, { shouldDirty: false });
    }
    setPendingTransition(null);
  }, [pendingTransition, form, user]);

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[640px]"
        data-testid="employees-detail-modal"
        // The role-transition AlertDialog is portalled and handles its
        // own focus — we let it sit ABOVE this dialog rather than nest.
      >
        <DialogHeader>
          <DialogTitle>Сотрудник</DialogTitle>
          <DialogDescription>
            {user?.status === 'PENDING'
              ? 'Сотрудник ещё не активировал приглашение.'
              : user?.status === 'ARCHIVED'
                ? 'Карточка в архиве — редактирование недоступно.'
                : 'Просмотр и изменение карточки.'}
          </DialogDescription>
        </DialogHeader>

        {userQuery.isError && !user ? (
          // Ф8 · В-Э, фикс-пак 1. Карточку теперь открывает и ссылка
          // (`/employees?card=<id>`), а по ссылке приходит и человек, которого
          // нет, и человек, которого актору сервер не отдаёт. Раньше оба случая
          // оставляли окно в вечной загрузке.
          <div
            role="alert"
            data-testid="employees-detail-load-error"
            className="flex flex-col items-start gap-3 rounded-sm border border-line bg-surface-2 p-4 text-sm text-ink"
          >
            <p>{cardErrorText(userQuery.error)}</p>
            <div className="flex flex-wrap gap-2">
              {isRetryableCardError(userQuery.error) ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="employees-detail-load-error-retry"
                  onClick={() => void userQuery.refetch()}
                >
                  {rightsRu.card.retry}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="employees-detail-load-error-close"
                onClick={() => onOpenChange(false)}
              >
                {rightsRu.card.close}
              </Button>
            </div>
          </div>
        ) : userQuery.isLoading || !user ? (
          <div className="space-y-2" data-testid="employees-detail-loading">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <>
            <header className="flex items-start gap-3">
              <span
                aria-hidden
                className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-accent text-base font-semibold text-accent-foreground"
              >
                {initialsOf(user.fullName)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-base font-semibold text-ink">{user.fullName}</p>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {statusBadge(user.status)}
                  <Badge variant={tierVariant(user.accessLevel)}>
                    {ROLE_NAMES[user.roleSlug] ?? user.roleSlug}
                  </Badge>
                </div>
              </div>
              {anyEditable && !editing && user.status !== 'ARCHIVED' ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="employees-detail-edit-toggle"
                  onClick={() => setEditing(true)}
                >
                  <Pencil className="mr-1 size-3.5" aria-hidden="true" />
                  Редактировать
                </Button>
              ) : null}
            </header>

            {editing ? (
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-3"
                  data-testid="employees-detail-form"
                >
                  <FormField
                    control={form.control}
                    name="fullName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>ФИО</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ''}
                            disabled={!matrix.fullName}
                            data-testid="employees-detail-fullName"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ''}
                            type="email"
                            disabled={!matrix.email}
                            data-testid="employees-detail-email"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Телефон</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ''}
                            disabled={!matrix.phone}
                            placeholder="+71234567890"
                            data-testid="employees-detail-phone"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="roleId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Роль</FormLabel>
                        <Select
                          value={field.value ? String(field.value) : ''}
                          onValueChange={(v) => field.onChange(Number(v))}
                          disabled={!matrix.role}
                        >
                          <SelectTrigger aria-label="Роль" data-testid="employees-detail-role">
                            <SelectValue placeholder="Выберите роль" />
                          </SelectTrigger>
                          <SelectContent>
                            {visibleAssignableRoles(actor, rolesQuery.data ?? [], 'edit').map(
                              (r) => (
                                <SelectItem key={r.id} value={String(r.id)}>
                                  {r.name}
                                </SelectItem>
                              ),
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* Feature 1 «Помощник ГИПа» (Wave 5). Shows ONLY when the
                      Admin changes the role TO ASSISTANT_GIP. Required — the
                      assistant must bind to a GIP. Changing the role away
                      clears the binding server-side. */}
                  {matrix.role && isAssistantGipSelected ? (
                    <FormField
                      control={form.control}
                      name="assistantOfUserId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ГИП</FormLabel>
                          <Select
                            // UI da#2 fix-pack: normalise the unset state to a
                            // single, stable empty value matching the ADD-modal
                            // idiom (`'' → undefined`). Mapping both `''` and
                            // `undefined` through the same `? :` ternary kept
                            // flipping the Radix Select between controlled and
                            // uncontrolled across renders (console churn).
                            value={!field.value ? undefined : String(field.value)}
                            onValueChange={(v) => field.onChange(v)}
                            disabled={gipUsersQuery.isLoading}
                          >
                            <SelectTrigger aria-label="ГИП" data-testid="employees-detail-gip">
                              <SelectValue placeholder="Выберите ГИПа" />
                            </SelectTrigger>
                            <SelectContent>
                              {gipPickerOptions.map((u) => (
                                <SelectItem key={u.id} value={u.id}>
                                  {u.fullName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {gipPickerOptions.length === 0 && !gipUsersQuery.isLoading ? (
                            <p
                              className="text-xs text-muted"
                              data-testid="employees-detail-gip-empty-hint"
                              aria-live="polite"
                            >
                              Нет активных ГИПов. Сначала назначьте ГИПа.
                            </p>
                          ) : null}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : null}
                  <FormField
                    control={form.control}
                    name="departmentId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Отдел</FormLabel>
                        <Select
                          value={
                            field.value === null || field.value === undefined
                              ? NULL_DEPT
                              : String(field.value)
                          }
                          onValueChange={(v) => field.onChange(v === NULL_DEPT ? null : Number(v))}
                          disabled={!matrix.department}
                        >
                          <SelectTrigger
                            aria-label="Отдел"
                            data-testid="employees-detail-department"
                          >
                            <SelectValue placeholder="Без отдела" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NULL_DEPT}>— без отдела —</SelectItem>
                            {(departmentsQuery.data ?? []).map((d) => (
                              <SelectItem key={d.id} value={String(d.id)}>
                                {d.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* R6.4 — joining date. Disabled per the field matrix
                      (EMPLOYEE never; РО for own-dept Сотрудник; Admin
                      for non-EMPLOYEE-tier). Stored as `YYYY-MM-DD`. */}
                  <FormField
                    control={form.control}
                    name="hireDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Дата приёма на работу</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ''}
                            type="date"
                            disabled={!matrix.hireDate}
                            data-testid="employees-detail-hireDate"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {formError ? (
                    <div
                      role="alert"
                      data-testid="employees-detail-error"
                      className="rounded-sm border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
                    >
                      {formError}
                    </div>
                  ) : null}

                  <DialogFooter>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={onCancelEdit}
                      data-testid="employees-detail-cancel"
                    >
                      <X className="mr-1 size-3.5" aria-hidden="true" />
                      Отмена
                    </Button>
                    <Button
                      type="submit"
                      data-testid="employees-detail-save"
                      disabled={!form.formState.isDirty || updateMutation.isPending}
                    >
                      Сохранить
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            ) : (
              <>
                <dl
                  className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-2 text-sm"
                  data-testid="employees-detail-grid"
                >
                  <dt className="text-muted">ФИО</dt>
                  <dd className="text-ink">{user.fullName}</dd>
                  <dt className="text-muted">Email</dt>
                  <dd className="text-ink">{user.email}</dd>
                  <dt className="text-muted">Телефон</dt>
                  <dd className="text-ink">{user.phone ?? '—'}</dd>
                  <dt className="text-muted">Отдел</dt>
                  <dd className="text-ink">
                    {user.departmentId
                      ? (departmentsQuery.data?.find((d) => d.id === user.departmentId)?.name ??
                        `#${user.departmentId}`)
                      : '—'}
                  </dd>
                  <dt className="text-muted">Дата приёма</dt>
                  <dd className="text-ink" data-testid="employees-detail-hireDate-readonly">
                    {/* R6.4 — `hireDate` is already a `YYYY-MM-DD` wire
                        string. `formatDate` accepts that shape directly. */}
                    {formatDate(user.hireDate, 'dd.MM.yyyy')}
                  </dd>
                  <dt className="text-muted">Создан</dt>
                  <dd className="text-ink">{formatDate(user.createdAt, 'dd.MM.yyyy')}</dd>
                  {user.archivedAt ? (
                    <>
                      <dt className="text-muted">Архивирован</dt>
                      <dd className="text-ink">{formatDate(user.archivedAt, 'dd.MM.yyyy')}</dd>
                    </>
                  ) : null}
                </dl>

                {/*
                  Блок 2 · Ф8 · В-Э — блок «Права» (Р-200, вариант Б: «права
                  человека видны в его карточке»). Стоит в режиме ПРОСМОТРА,
                  между сведениями и действиями: права — свойство человека, а
                  не поле формы, и пишутся своим маршрутом по одному, мимо
                  кнопки «Сохранить».

                  ⚠️ Только управляющему правами (`canManageRights` — штатный
                  супервайзер). Остальным блок не монтируется вовсе: «места
                  просто нет» (Р-200), и запрос, на который сервер ответил бы
                  403, не уходит. Что показать внутри — в том числе
                  не-получателю — решает сам блок по ответу сервера.

                  ⚠️ В режиме правки блока нет намеренно: смена должности
                  сбрасывает права на сервере (Р-198), и после сохранения блок
                  монтируется заново и перечитывает их.
                */}
                {canManageRights(actor) ? <UserRightsBlock userId={user.id} /> : null}

                {/*
                  R6.13 / D-01 — gate destructive actions through the same
                  RBAC helpers the list-row dropdown uses (`canArchive` &
                  siblings live in `../employees-columns`). Previously this
                  footer rendered every action whenever the row was in the
                  matching status, which surfaced an «Архивировать» button
                  for ADMIN actors on EMPLOYEE-tier targets — the BE then
                  403'd with `admin_cannot_archive_employee_tier`. The
                  helpers already encode the full matrix (status + actor
                  tier + target tier + dept match + self-exclude), so we
                  only need to gate the buttons here and the FE stays
                  aligned with the BE source of truth.

                  R6.13 fix-pack v2 (UI HIGH-1) — additionally hide the
                  whole <DialogFooter> when EVERY action evaluates to
                  hidden (EMPLOYEE actor on own card, certain SUPERVISOR
                  combos, etc). Otherwise the footer's `gap-2` strip
                  rendered an 8px dead band below the grid.
                */}
                {(() => {
                  const showReissue = Boolean(onReissueInvite) && canReissueInvite(user, actor);
                  const showReset = Boolean(onIssueReset) && canIssueReset(user, actor);
                  const showArchive = Boolean(onArchive) && canArchive(user, actor);
                  const showHardDelete =
                    Boolean(onHardDelete) && canHardDelete(user, actor, actor.id);
                  const showAny = showReissue || showReset || showArchive || showHardDelete;
                  if (!showAny) return null;
                  return (
                    <DialogFooter data-testid="employees-detail-footer">
                      {showReissue ? (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => onReissueInvite!(user)}
                          data-testid="employees-detail-reissue"
                        >
                          Перевыпустить приглашение
                        </Button>
                      ) : null}
                      {showReset ? (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => onIssueReset!(user)}
                          data-testid="employees-detail-reset"
                        >
                          Сбросить пароль
                        </Button>
                      ) : null}
                      {showArchive ? (
                        <Button
                          type="button"
                          variant="destructive"
                          onClick={() => onArchive!(user)}
                          data-testid="employees-detail-archive"
                        >
                          Архивировать
                        </Button>
                      ) : null}
                      {showHardDelete ? (
                        <Button
                          type="button"
                          variant="destructive"
                          onClick={() => onHardDelete!(user)}
                          data-testid="employees-detail-hard-delete"
                        >
                          Удалить
                        </Button>
                      ) : null}
                    </DialogFooter>
                  );
                })()}
              </>
            )}
          </>
        )}

        <RoleTransitionAlertDialog
          context={pendingTransition}
          open={pendingTransition !== null}
          onCancel={onTransitionCancel}
          onConfirm={onTransitionConfirm}
        />
      </DialogContent>
    </Dialog>
  );
}
